/**
 * Coursewide completions shard path helpers: the per-course aggregate of
 * every student's `assnProgress` map that the Overall Completions admin page
 * reads. No Firestore dependency; safe to use from client or server.
 * Uses hashStringTo32Bit for backwards compatibility with existing shards.
 */
import {z} from 'zod';
import {hashStringTo32Bit} from '../../hash';

/** Number of shards per course; changing it moves students between shards. */
export const COURSEWIDE_COMPLETIONS_SHARD_COUNT = 100;

/** Id prefix of the assignment shards in `course/{courseId}/completions`. */
export const COURSEWIDE_ASSIGNMENT_SHARD_PREFIX = 'assignments-';

/** Id prefix of the lesson shards in `course/{courseId}/completions`. */
export const COURSEWIDE_LESSON_SHARD_PREFIX = 'lessons-';

/**
 * One assignment or lesson entry in a user's shard map. Entries written
 * before completion times were recorded are bare booleans; newer entries
 * carry the first completion time, so readers must accept both.
 */
export const CompletionShardEntrySchema = z.union([
  z.boolean(),
  z.object({
    /** Epoch milliseconds of the user's first completion. */
    completedAt: z.number(),
  }),
]);

/** A user's shard entry: `true`, `false`, or `{completedAt}`. */
export type CompletionShardEntry = z.infer<typeof CompletionShardEntrySchema>;

/** A user's shard map: assignment or lesson id → {@link CompletionShardEntry}. */
export const CompletionShardUserMapSchema = z.record(
  z.string(),
  CompletionShardEntrySchema,
);

/** A user's shard map, keyed by assignment or lesson id. */
export type CompletionShardUserMap = z.infer<
  typeof CompletionShardUserMapSchema
>;

function shardIndexFor(userId: string): number {
  // Existing shards were placed with this hash; keep it so users stay put.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const userHash = hashStringTo32Bit(userId);
  return Math.abs(userHash) % COURSEWIDE_COMPLETIONS_SHARD_COUNT;
}

/**
 * Returns the Firestore document path of the coursewide completions shard
 * that holds the given user. Each shard maps user ids to that user's
 * `users/{userId}/{courseId}/assnProgress` map.
 *
 * @param params - Path parameters.
 * @param params.courseId - The course identifier.
 * @param params.userId - The Firebase Auth UID of the user.
 * @return The absolute Firestore document path string.
 * @throws Never.
 */
export function getCoursewideCompletionsShardDocPath({
  courseId,
  userId,
}: {
  courseId: string;
  userId: string;
}): string {
  return `course/${courseId}/completions/${COURSEWIDE_ASSIGNMENT_SHARD_PREFIX}${String(shardIndexFor(userId))}`;
}

/**
 * Returns the Firestore document path of the coursewide lesson completions
 * shard that holds the given user. Lesson shards sit in the same
 * `completions` collection as the assignment shards (same rules, same
 * placement), told apart by their id prefix. Each maps user ids to that
 * user's {@link summarizeLessonsProgress} map.
 *
 * @param params - Path parameters.
 * @param params.courseId - The course identifier.
 * @param params.userId - The Firebase Auth UID of the user.
 * @return The absolute Firestore document path string.
 * @throws Never.
 */
export function getCoursewideLessonCompletionsShardDocPath({
  courseId,
  userId,
}: {
  courseId: string;
  userId: string;
}): string {
  return `course/${courseId}/completions/${COURSEWIDE_LESSON_SHARD_PREFIX}${String(shardIndexFor(userId))}`;
}

/**
 * Reduces a user's `users/{userId}/{courseId}/lessonsProgress` document to
 * the lessons they completed. The document also holds one key per finished
 * slide (`lessonId/slideId`); only the bare lesson ids set to `true` count.
 *
 * @param progress - The document's data, or `undefined` when it is absent.
 * @return Lesson id → `true`, one entry per completed lesson.
 * @throws Never.
 */
export function summarizeLessonsProgress(
  progress: Readonly<Record<string, unknown>> | undefined,
): Record<string, true> {
  const out: Record<string, true> = {};
  for (const [key, value] of Object.entries(progress ?? {})) {
    if (value === true && !key.includes('/')) out[key] = true;
  }
  return out;
}

/**
 * Tells whether a shard entry (or a raw `assnProgress` value) marks the
 * assignment or lesson as completed. `true` and `{completedAt}` count;
 * `false`, missing, and anything malformed do not.
 *
 * @param entry - The stored value, of any shape.
 * @return `true` when the entry is a completion.
 * @throws Never.
 */
export function isCompletionEntryComplete(entry: unknown): boolean {
  if (entry === true) return true;
  const parsed = CompletionShardEntrySchema.safeParse(entry);
  return parsed.success && typeof parsed.data === 'object';
}

/**
 * Reads the first completion time off a shard entry.
 *
 * @param entry - The stored value, of any shape.
 * @return Epoch milliseconds, or `undefined` for a boolean entry, an
 *   incomplete entry, or a malformed one.
 * @throws Never.
 */
export function completionEntryTimeMs(entry: unknown): number | undefined {
  const parsed = CompletionShardEntrySchema.safeParse(entry);
  if (!parsed.success || typeof parsed.data !== 'object') return undefined;
  return parsed.data.completedAt;
}

/** The shard fields a progress change implies for one user. */
export interface CompletionShardDelta {
  /** Ids newly completed (stamped with `nowMs`) or newly un-completed. */
  set: Record<string, CompletionShardEntry>;
  /** Ids whose key vanished from the progress map; the trigger deletes them. */
  remove: string[];
}

/**
 * Computes what a trigger must write to a user's shard map after their
 * progress map changed from `before` to `after`. Ids that were and still are
 * complete are left alone so an existing `completedAt` survives; ids that
 * became complete are stamped `nowMs`; ids that flipped to a non-complete
 * value are set to `false`; ids that disappeared are listed for removal.
 *
 * @param params - The change.
 * @param params.before - Progress map before the write; `undefined` when
 *   the document was just created.
 * @param params.after - Progress map after the write; `undefined` when the
 *   document was deleted, which removes every id.
 * @param params.nowMs - The write's server timestamp in epoch milliseconds.
 * @return The {@link CompletionShardDelta}; both parts empty when nothing
 *   relevant changed.
 * @throws Never.
 */
export function diffCompletionProgress({
  before,
  after,
  nowMs,
}: {
  before: Readonly<Record<string, unknown>> | undefined;
  after: Readonly<Record<string, unknown>> | undefined;
  nowMs: number;
}): CompletionShardDelta {
  const delta: CompletionShardDelta = {set: {}, remove: []};
  const previous = before ?? {};
  const next = after ?? {};
  for (const [id, value] of Object.entries(next)) {
    const wasComplete = isCompletionEntryComplete(previous[id]);
    const isComplete = isCompletionEntryComplete(value);
    if (isComplete && !wasComplete) delta.set[id] = {completedAt: nowMs};
    else if (!isComplete && wasComplete) delta.set[id] = false;
  }
  for (const id of Object.keys(previous)) {
    if (!(id in next)) delta.remove.push(id);
  }
  return delta;
}
